import { observer } from 'mobx-react'
import React from 'react'
import cn from 'classnames'

import { APIStatus } from '@flowwai-frontend/shared/api-common'
import {
   Button,
   Hierarchy,
   MarkdownContent,
   SubVariant
} from '@flowwai-frontend/design-system'
import { useAppTheme } from '@flowwai-frontend/bps-officer-web/style-guide'
import { useCustomT } from '@flowwai-frontend/shared/common-components'
import { isAPIFetching } from '@flowwai-frontend/shared/utils/common'

import {
   ConfirmButtonStyles,
   container,
   deleteButtonsContainerClassName,
   descriptionStyles,
   headingAndDescriptionContainer,
   headingStyles,
   iconAndDetailsContainer,
   leaveButtonStyles
} from './styles'

interface ConfirmationCardProps {
   apiStatus: APIStatus
   description: string
   heading: string

   shouldRenderDescriptionWithMarkdown?: boolean
   closeModal: () => void
   onDelete: () => void
   containerClassName?: string
   cancelButtonText?: string
   confirmButtonSubVariant?: SubVariant
   confirmButtonText?: string
   shouldShowCancelButton?: boolean
   headingAndDescriptionContainerStyles?: string
   reverseButtonsOrder?: boolean

   renderHeadingBottomEnhancer?: () => React.ReactElement
   renderBody?: () => React.ReactElement
   renderIcon?: () => React.ReactElement
}

const ConfirmationCard = (props: ConfirmationCardProps): React.ReactElement => {
   const {
      apiStatus,
      description,
      heading,

      closeModal,
      onDelete,

      cancelButtonText,
      confirmButtonSubVariant,
      confirmButtonText,
      shouldRenderDescriptionWithMarkdown = false,
      shouldShowCancelButton = true,
      reverseButtonsOrder,
      containerClassName,
      renderHeadingBottomEnhancer,
      renderIcon
   } = props

   const buttonsText = useCustomT('boards:common')

   const { theme } = useAppTheme()

   const boardTheme = theme.common.deleteCard

   const isLoading = isAPIFetching(apiStatus)

   const subVariant = confirmButtonSubVariant
      ? confirmButtonSubVariant
      : SubVariant.Destructive

   const renderCancelButton = () => (
      <Button
         hierarchy={Hierarchy.Secondary}
         subVariant={SubVariant.GrayOutline}
         size={'Large'}
         className={cn(leaveButtonStyles, reverseButtonsOrder ? '' : 'mr-12px')}
         onClick={closeModal}
         isDisabled={isLoading}
         autoFocus
      >
         {cancelButtonText ? cancelButtonText : buttonsText('leave')}
      </Button>
   )

   const renderConfirmationButton = () => (
      <Button
         hierarchy={Hierarchy.Primary}
         subVariant={subVariant}
         size={'Large'}
         className={cn(
            ConfirmButtonStyles,
            reverseButtonsOrder ? 'mr-[12px]' : ''
         )}
         onClick={onDelete}
         isDisabled={isLoading}
         isLoading={isLoading}
      >
         {confirmButtonText ? confirmButtonText : buttonsText('yesDelete')}
      </Button>
   )

   const renderDescription = (): React.ReactElement | null => {
      if (!description) return null

      return shouldRenderDescriptionWithMarkdown ? (
         <MarkdownContent
            content={description}
            className={descriptionStyles(boardTheme)}
         />
      ) : (
         <span className={descriptionStyles(boardTheme)}>{description}</span>
      )
   }

   return (
      <div className={cn(container, containerClassName)}>
         <div className={iconAndDetailsContainer}>
            <div>{renderIcon ? renderIcon() : ''}</div>

            <div
               className={cn(
                  headingAndDescriptionContainer,
                  props.headingAndDescriptionContainerStyles
               )}
            >
               <span className={headingStyles(boardTheme)}>{heading}</span>
               {renderHeadingBottomEnhancer?.()}

               {/* NOTE: if empty string occurs, not rendering element */}
               {renderDescription()}
            </div>
            {props.renderBody?.()}

            <div className={deleteButtonsContainerClassName}>
               {reverseButtonsOrder ? (
                  <>
                     {renderConfirmationButton()}
                     {shouldShowCancelButton ? renderCancelButton() : null}
                  </>
               ) : (
                  <>
                     {shouldShowCancelButton ? renderCancelButton() : null}
                     {renderConfirmationButton()}
                  </>
               )}
            </div>
         </div>
      </div>
   )
}

export default observer(ConfirmationCard)
